import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { CategoryFormComponent } from '../category-form/category-form.component';
import { ListProductCategoriesResponse, ProductCategoryResponse } from '../../models';
import { ProductCategoryService } from '../../services';

@Component({
  selector: 'lib-category-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    CategoryFormComponent
  ],
  providers: [ConfirmationService],
  template: `
    <p-dialog 
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '900px', height: '80vh' }"
      (onHide)="onClose.emit()">
      
      <ng-template pTemplate="header">
        <h3>{{ 'RETAIL_BILLING.CATEGORY.MANAGE' | translate }}</h3>
      </ng-template>

      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <p-iconField iconPosition="left">
            <p-inputIcon styleClass="pi pi-search"></p-inputIcon>
            <input 
              pInputText 
              type="text" 
               [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
              [(ngModel)]="searchTerm"
              (ngModelChange)="onSearch()"
              aria-label="search-categories-input" />
          </p-iconField>
          
          <p-button
            [label]="'RETAIL_BILLING.CATEGORY.CREATE' | translate"
            icon="pi pi-plus"
            (onClick)="showCreateDialog()"
            aria-label="create-category-button">
          </p-button>
        </div>

        <!-- Categories table -->
        <p-table 
          [value]="searchCategories" 
          [loading]="loading"
          [paginator]="false"
          [rows]="pageSize"
          [totalRecords]="totalRecords"
          [lazy]="true"
          (onLazyLoad)="loadCategories($event)"
          [showCurrentPageReport]="true"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [rowsPerPageOptions]="[10, 25, 50]"
          styleClass="p-datatable-gridlines">
          
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'RETAIL_BILLING.CATEGORY.NAME' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.CATEGORY.DESCRIPTION' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="body" let-category>
            <tr>
              <td>
                <div class="flex align-items-center">
                  <img 
                    *ngIf="category.image_url" 
                    [src]="category.image_url" 
                    [alt]="category.name_primary_language"
                    class="w-3rem h-3rem mr-2 border-round" />
                  <div>
                    <div class="font-medium">{{ category.name_primary_language }}</div>
                    <div class="text-sm text-500">{{ category.name_secondary_language }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div>
                  <div class="font-medium">{{ category.description_primary_language }}</div>
                  <div class="text-sm text-500">{{ category.description_secondary_language }}</div>
                </div>
              </td>
              <td>
                <p-tag 
                  [value]="category.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                  [severity]="category.is_active ? 'success' : 'danger'">
                </p-tag>
              </td>
              <td>
                <div class="flex gap-2">
                  <p-button
                    icon="pi pi-pencil"
                    tooltipPosition="top"
                    tooltip="Edit Category"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    (onClick)="editCategory(category)"
                    [ariaLabel]="'edit-category-' + category.id">
                  </p-button>
                  <p-button *ngIf="false" 
                    icon="pi pi-trash"
                    tooltipPosition="top"
                    tooltip="Delete Category"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    severity="danger"
                    (onClick)="deleteCategory(category)"
                    [ariaLabel]="'delete-category-' + category.id">
                  </p-button>
                </div>
              </td>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="text-center">{{ 'RETAIL_BILLING.CATEGORY.NO_CATEGORIES' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-content-end">
          <p-button 
            [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
            icon="pi pi-times"
            [text]="true"
            (onClick)="onClose.emit()"
            aria-label="close-button">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Create/Edit Category Dialog -->
    <lib-category-form
      *ngIf="showCategoryDialog"
      [(visible)]="showCategoryDialog"
      [category]="selectedCategory"
      [companyLevel]="companyLevel"
      [tenantId]="tenantId"
      [primaryLanguage]="primaryLanguage"
      [secondaryLanguage]="secondaryLanguage"
      [isSecondaryLanguage]="isSecondaryLanguage"
      (onSave)="onCategorySaved($event)"
      (onCancel)="hideCategoryDialog()">
    </lib-category-form>

    <p-confirmDialog></p-confirmDialog>
    <p-toast></p-toast>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryListComponent implements OnInit {
  @Input() visible = false;
  @Input() companyLevel: boolean = true;
  @Input() tenantId?: number;
  @Input() primaryLanguage: string = 'English';
  @Input() secondaryLanguage: string = 'Arabic';
  @Input() isSecondaryLanguage: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<void>();

  categories: ProductCategoryResponse[] = [];
  searchCategories: ProductCategoryResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  searchTerm = '';
  
  showCategoryDialog = false;
  selectedCategory: ProductCategoryResponse | null = null;

  constructor(
    private categoryService: ProductCategoryService,
    private toastService: ToastMessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    
  }



  loadCategories(event?: any) {
    this.loading = true;

    this.categoryService.getCompanyCategories().subscribe({
      next: (response: ListProductCategoriesResponse) => {
        this.categories = response.items || [];
        this.searchCategories = response.items || [];
        this.totalRecords = response.total_items;
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  onSearch() {
    this.searchCategories = this.categories.filter(category => 
      category.name_primary_language?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
      category.name_secondary_language?.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
    this.changeDetectorRef.detectChanges();
  }

  showCreateDialog() {
    this.selectedCategory = null;
    this.showCategoryDialog = true;
  }

  editCategory(category: ProductCategoryResponse) {
    this.selectedCategory = category;
    this.showCategoryDialog = true;
  }

  deleteCategory(category: ProductCategoryResponse) {
    this.confirmationService.confirm({
      message: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DELETE'),
      header: this.translate.instant('RETAIL_BILLING.CATEGORY.DELETE'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        // TODO: Implement delete functionality
        this.toastService.showSuccess(this.translate.instant('RETAIL_BILLING.CATEGORY.DELETE_SUCCESS'));
        this.loadCategories();
      }
    });
  }

  onCategorySaved(category: ProductCategoryResponse) {
    this.hideCategoryDialog();
    this.loadCategories();
    const message = this.selectedCategory 
      ? 'RETAIL_BILLING.CATEGORY.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.CATEGORY.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideCategoryDialog() {
    this.showCategoryDialog = false;
    this.selectedCategory = null;
  }
}